import { Component, Inject, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { HvlAbstractLazyList } from '@hvlng/framework-core/form';
import { HvlFormGroup } from '@hvlng/framework-bff/core';
import { LazyLoadEvent } from 'primeng/api';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subscription } from 'rxjs';
import { ModalConfig, UiTableHeaderModel, UiToastMessageService, UiTranslateLoaderService } from 'ui-shared';
import { PerPersonnelQueryModel } from '../../personnel-operations/model/per-personnel-query-model';
import { PerPersonnelModel } from '../../personnel-operations/model/per-personnel-model';
import { PerPersonnelService } from '../../personnel-operations/service/per-personnel-service';
import { PageCode } from 'humanresources-common-shared';
import { HvlSecurityService } from '@hvlng/framework-bff/security';

import { PersonnelAdvancedSearchFormComponent } from '../../personnel-advanced-search/form/components/personnel-advanced-search-form.component';
import { PerAdvancedSearchEntityModel } from '../../personnel-operations/model/per-advanced-search-entity-model';
import { PerAdvancedSearchRuleModel } from '../../personnel-operations/model/per-advanced-search-rule-model';
import { PersonnelSharedService } from '../../../service/personnel-shared-service';
import { CUSTOM_DIALOG_SERVICE } from '../service/hr-personnel-picker-replay-subject.service';
import { Validators } from '@angular/forms';

@Component({
    selector: 'app-personnel-picker-list',
    templateUrl: './personnel-picker-list.component.html',
    providers: [
        {
            provide: CUSTOM_DIALOG_SERVICE,
            useClass: DialogService,
        },
    ],
})
export class PersonnelPickerListComponent extends HvlAbstractLazyList<PerPersonnelModel> implements OnInit, OnDestroy {
    rowsPerPageOptions = [];

    selectedModel;
    form = new HvlFormGroup<PerPersonnelQueryModel>(new PerPersonnelQueryModel());
    listSubscription: Subscription;
    list;
    pageCode = PageCode.PERSONNEL_PICKER;
    isClear: boolean;
    selectionMode;
    extSetting;
    columns: UiTableHeaderModel[] = [];
    pageReady = false;
    queryModelPersonnelModelMap;
    queryModel;
    activeFieldDisable;
    hasFullAuthority: boolean;
    applyAuthorization: boolean;
    addButtonLabel: string = 'addSelected';
    isIdentityNumberRequired: boolean;
    authorizationRoleTypeCodeList: string[];
    showTopOrg = false;

    authorizationRoleTypeCode;
    advancedSearchQueryModel = [];
    advancedSearchList = [];
    searchResult = null;
    totalElements = 0;
    refAd: DynamicDialogRef;

    constructor(
        protected translateService: UiTranslateLoaderService,
        private uiToastMessageService: UiToastMessageService,
        private service: PersonnelSharedService,
        @Inject(CUSTOM_DIALOG_SERVICE) private dialogService: DialogService,
        @Optional() private dialogConfig: DynamicDialogConfig,
        private personnelService: PerPersonnelService,
        private securityService: HvlSecurityService,
        protected dynamicDialogRef: DynamicDialogRef,
    ) {
        super();
    }

    ngOnInit(): void {
        this.list = [];
        this.isClear = false;
        this.selectionMode = this.dialogConfig.data.selectionMode;
        this.queryModel = this.dialogConfig.data.queryModel;
        this.rowsPerPageOptions = this.dialogConfig.data.rowsPerPageOptions;
        if (this.rowsPerPageOptions == undefined || this.rowsPerPageOptions.length == 0) {
            this.rowsPerPageOptions = [10, 50, 100];
        }
        this.applyAuthorization = this.dialogConfig.data.applyAuthorization;
        this.addButtonLabel = this.dialogConfig.data.addButtonLabel;
        this.isIdentityNumberRequired = this.dialogConfig.data.isIdentityNumberRequired;
        this.showTopOrg = this.dialogConfig.data.showTopOrg;
        this.form.get('identificationNumber').clearValidators();
        if (this.isIdentityNumberRequired === true) {
            this.form.get('identificationNumber').addValidators(Validators.required);
        }
        this.form.get('identificationNumber').updateValueAndValidity();
        if (this.queryModel) {
            this.form.patchValue(this.queryModel);
            if (this.queryModel?.active != null) {
                this.activeFieldDisable = true;
            }
        }
        this.checkInitialData();

        this.hasFullAuthority = this.securityService.hasAuthority('kovan:hr:personnel:query');

        this.columns = [
            { header: this.translateService.instant('hrPerPersonnel.beginDate'), field: 'beginDate' },
            { header: this.translateService.instant('hrPerPersonnel.endDate'), field: 'endDate' },
            { header: this.translateService.instant('hrPerPersonnel.registryNumber'), field: 'registryNumber' },
            // this.hasFullAuthority && {
            //     header: this.translateService.instant('hrPerPersonnel.identificationNumber'),
            //     field: 'identificationNumber',
            // },
            { header: this.translateService.instant('hrPerCommon.name'), field: 'name' },
            { header: this.translateService.instant('hrPerCommon.surname'), field: 'surname' },
            {
                header: this.translateService.instant('hrPerCommon.perPersonnelOrganization'),
                field: 'perPersonnelOrganization',
            },
            { header: this.translateService.instant('hrPerPersonnel.position'), field: 'perPersonnelPosition' },
            { header: this.translateService.instant('hrPerPersonnel.employeeTypeUuid'), field: 'perEmployeeType' },
            {
                header: this.translateService.instant('hrPerPersonnel.employeeSubtypeUuid'),
                field: 'perEmployeeSubtype',
            },
            { header: this.translateService.instant('hrPerPersonnel.workAreaUuid'), field: 'perWorkArea' },
            { header: this.translateService.instant('hrPerPersonnel.workSubareaUuid'), field: 'perWorkSubarea' },
            { header: this.translateService.instant('hrPerPersonnel.statusText'), field: 'statusText' },
            { header: this.translateService.instant('hrPerPersonnel.startDate'), field: 'startDate' },
            { header: this.translateService.instant('hrPerPersonnel.activeEmployee'), field: 'isActive' },
        ].filter(Boolean);
        this.personnelService
            .readSettingFile('/assets/' + this.pageCode + '.json')
            .then((res) => {
                if (res == null) return;
                this.extSetting = res;
                this.queryModelPersonnelModelMap = res;
                this.pageReady = true;
                this.extSetting.default.forEach((item, index) => {
                    switch (item.key) {
                        case 'employeeTypeUuid':
                            this.queryModelPersonnelModelMap.default[index].key = 'perEmployeeType';
                            break;

                        case 'employeeSubtypeUuid':
                            this.queryModelPersonnelModelMap.default[index].key = 'perEmployeeSubtype';
                            break;
                        case 'workAreaUuid':
                            this.queryModelPersonnelModelMap.default[index].key = 'perWorkArea';
                            break;
                        case 'workSubareaUuid':
                            this.queryModelPersonnelModelMap.default[index].key = 'perWorkSubarea';
                            break;
                        case 'positionId':
                            this.queryModelPersonnelModelMap.default[index].key = 'perPersonnelPosition';
                            break;
                        default:
                            break;
                    }
                });
            })
            .finally(() => {
                this.pageReady = true;
            });
    }

    lazyLoadData(event: LazyLoadEvent) {
        if (event?.sortField && this.list.length == 0) {
            return;
        }
        if (this.authorizationRoleTypeCodeList && this.authorizationRoleTypeCodeList.length > 0)
            this.form.get('authorizationRoleTypeCodeList').setValue(this.authorizationRoleTypeCodeList);

        if (this.form.get('identificationNumber').value == '') {
            this.form.get('identificationNumber').setValue(null);
        }
        this.preparePageable(event, this.form);
        if (this.form.value.registryNumber && this.form.value.registryNumber.length > 0) {
            this.form.value.registryNumber = this.form.value.registryNumber.map((item: string) =>
                item.replace(/[\n\t\r]/g, '').trim(),
            );
        }
        this.listSubscription = this.service.queryPersonnelPage(this.form.value).subscribe((response) => {
            this.list = response.data;
            this.totalElements = response.totalElements;
            this.totalPages = response.totalPages;
            if (this.form.value.pageable.pageNumber == 0) {
                this.checkInitialData();
            }


            if (this.selectedModel && this.selectedModel.length > 0) {
                const currentKeys = new Set(this.list.map(item => item.compositeKey));
                const previousSelections = this.selectedModel.filter(item => !currentKeys.has(item.compositeKey));

                const currentSelections = this.selectedModel
                    .map(item => this.list.find(listItem => listItem.compositeKey === item.compositeKey))
                    .filter(Boolean);
                this.selectedModel = [...previousSelections, ...currentSelections];
            }

            this.dialogConfig.data.list = this.list;
        });
    }

    loadListDataAdvancedSearch() {
        this.personnelService.genericQueryBuilder(this.advancedSearchQueryModel).subscribe((response) => {
            this.advancedSearchList = response;
            this.totalElements = response?.length || 0;
            this.checkInitialData();


        });
    }

    checkInitialData() {
        if (this.dialogConfig.data.personnelPickerData && (!this.selectedModel || this.selectedModel.length === 0)) {
            if (Array.isArray(this.dialogConfig.data.personnelPickerData)) {
                this.selectedModel = this.dialogConfig.data.personnelPickerData;
            } else {
                this.selectedModel = [this.dialogConfig.data.personnelPickerData];
            }
        }
    }

    onRowSelectClickEvent(result?: any) {
        if (this.selectionMode == 'select-all') {
            if (this.dynamicDialogRef) this.dynamicDialogRef.close(this.form);
        } else {
            if (!this.selectedModel) {
                this.uiToastMessageService.showWarn(this.translateService.instant('toastMessage.chooseDataFromTable'));
            } else {
                if (this.dynamicDialogRef) {
                    this.dynamicDialogRef.close({ selectedModel: this.selectedModel, list: this.list });
                } else this.onRowSelectEventEmitter.emit(this.selectedModel[0]);
            }
        }
    }

    ngOnDestroy() {
        if (this.listSubscription) this.listSubscription.unsubscribe();
    }

    rowSelect(e) {}

    onCancel() {
        this.selectedModel = null;
        this.dynamicDialogRef.close();
    }

    onSearch() {
        const previousSelections = this.selectedModel ? [...this.selectedModel] : [];
        if (this.advancedSearchQueryModel.length > 0) {
            this.checkFilterIsEmpty();
            this.loadListDataAdvancedSearch();
        } else {
            this.dataTableRef.reset();
        }
        this.selectedModel = previousSelections;
    }

    checkFilterIsEmpty() {
        let arr = [];
        Object.entries(this.form.value).forEach(([key, value]) => {
            if (value) {
                arr.push({ field: key, value: value });
            }
        });
        let detailSearch = [];
        arr.forEach((item) => {
            let perAdvancedSearchEntityModel = new PerAdvancedSearchEntityModel();
            let perAdvancedSearchRuleModel = new PerAdvancedSearchRuleModel();
            perAdvancedSearchEntityModel.condition = 'and';
            switch (item.field) {
                case 'identificationNumber':
                    perAdvancedSearchEntityModel.entity = 'PerIdentity';
                    perAdvancedSearchEntityModel.rules = [];
                    perAdvancedSearchRuleModel.entity = 'PerIdentity';
                    perAdvancedSearchRuleModel.field = 'identificationNumber';
                    perAdvancedSearchRuleModel.operator = '=';
                    perAdvancedSearchRuleModel.type = 'String';
                    perAdvancedSearchRuleModel.value = item.value;
                    perAdvancedSearchEntityModel.rules.push(perAdvancedSearchRuleModel);
                    break;
                case 'name':
                    perAdvancedSearchEntityModel.entity = 'PerIdentity';
                    perAdvancedSearchEntityModel.rules = [];
                    perAdvancedSearchRuleModel.entity = 'PerIdentity';
                    perAdvancedSearchRuleModel.field = 'name';
                    perAdvancedSearchRuleModel.operator = 'like';
                    perAdvancedSearchRuleModel.type = 'String';
                    perAdvancedSearchRuleModel.value = item.value;
                    perAdvancedSearchEntityModel.rules.push(perAdvancedSearchRuleModel);
                    break;
                case 'surname':
                    perAdvancedSearchEntityModel.entity = 'PerIdentity';
                    perAdvancedSearchEntityModel.rules = [];
                    perAdvancedSearchRuleModel.entity = 'PerIdentity';
                    perAdvancedSearchRuleModel.field = 'surname';
                    perAdvancedSearchRuleModel.operator = 'like';
                    perAdvancedSearchRuleModel.type = 'String';
                    perAdvancedSearchRuleModel.value = item.value;
                    perAdvancedSearchEntityModel.rules.push(perAdvancedSearchRuleModel);
                    break;
                case 'startDate':
                    perAdvancedSearchEntityModel.entity = 'PerPeople';
                    perAdvancedSearchEntityModel.rules = [];
                    perAdvancedSearchRuleModel.entity = 'PerPeople';
                    perAdvancedSearchRuleModel.field = 'startDate';
                    perAdvancedSearchRuleModel.operator = '=';
                    perAdvancedSearchRuleModel.type = 'LocalDate';
                    perAdvancedSearchRuleModel.value = item.value;
                    perAdvancedSearchEntityModel.rules.push(perAdvancedSearchRuleModel);
                    break;
                default:
                    break;
            }
            if (this.findFilterParameter(item.field) === undefined) {
                detailSearch.push(perAdvancedSearchEntityModel);
            }
        });
        const exists = this.advancedSearchQueryModel.some((obj) => this.isEqual(obj, detailSearch));
        if (!exists) {
            this.advancedSearchQueryModel.push(...detailSearch);
        }
    }

    isEqual(obj1, obj2) {
        return JSON.stringify(obj1) === JSON.stringify(obj2);
    }

    findFilterParameter(fieldName) {
        let map = this.advancedSearchQueryModel.map((item) => item.rules[0]);
        return map.find((item) => item.field == fieldName);
    }

    onClear() {
        this.isClear = true;
        if (this.queryModel) {
            this.form.patchValue(this.queryModel);
            if (this.queryModel?.active != null) {
                this.activeFieldDisable = true;
            }
        } else {
            this.form.reset();
        }
        this.advancedSearchQueryModel = [];

        this.onSearch();
    }

    checkCol(field) {
        if (this.extSetting) {
            let fieldItem = this.queryModelPersonnelModelMap.default.find((item) => item.key == field);
            if (fieldItem) {
                return fieldItem.display;
            }
            return true;
        }
        return true;
    }

    openDetailSearch() {
        this.refAd = this.dialogService.open(PersonnelAdvancedSearchFormComponent, {
            width: ModalConfig.MODAL_WIDTH,
            header: this.translateService.instant('hrPerPersonnel.detailSearch.perDetailSearch'),
            appendTo: 'body',
            contentStyle: {
                height: ModalConfig.MODAL_MAX_HEIGHT,
                'max-height': ModalConfig.MODAL_MAX_HEIGHT,
                overflow: 'auto',
            },
            data: { advancedSearchQueryModel: this.advancedSearchQueryModel },
        });
        this.refAd.onClose.subscribe((model: any) => {
            if (model && model?.length != 0) {
                this.advancedSearchQueryModel = model;
                this.form.reset();
            } else {
                this.advancedSearchList = [];
                this.advancedSearchQueryModel = [];
            }
        });
    }
}
